import { HStack, Text, VStack } from '@direct-frontend/components';
import { type OrderProductVariant } from '@/shared/api/order';
import { Alert, type AlertProps } from '@/shared/components/Alert';
import { Drawer, DrawerBottomFixed, DrawerButton, DrawerHeader } from '@/shared/components/Drawer';
import { getProductImage } from '@/shared/lib/getProductImage';
import { getProductSize } from '@/shared/lib/getProductSize';
import { type InsufficientItem } from '@/shared/lib/hooks';
import { getRequestContext } from '@/shared/lib/requestContext';
import { OutdatedStockItem } from './OutdatedStockItem';
import styles from './OutdatedStockModal.module.css';

interface OutdatedStockModalProps extends AlertProps {
    insufficientItems?: InsufficientItem[];
    orderItems: OrderProductVariant[];
    onUpdateOrder: () => void;
}

export const OutdatedStockModal = (props: OutdatedStockModalProps) => {
    const TITILE = 'Некоторые товары уже купили';
    const DESCRIPTION =
        'Пока вас не было, некоторые товары стали недоступны. Проверьте корзину и выберите альтернативу.';
    const { description, button, insufficientItems, orderItems, errorText, ...other } = props;
    const {
        company: { companyShopUrl },
    } = getRequestContext();

    if (!insufficientItems) {
        return (
            <Alert
                {...props}
                title={TITILE}
                description={DESCRIPTION}
                button={
                    <DrawerBottomFixed>
                        <DrawerButton href={companyShopUrl}>Обновить корзину</DrawerButton>
                    </DrawerBottomFixed>
                }
            />
        );
    }

    if (insufficientItems.length === orderItems.length && insufficientItems.every((item) => item.available === 0)) {
        return (
            <Alert
                {...props}
                title="Все товары закончились"
                description="Пока вы оформляли заказ, все выбранные товары уже выкупили. Пожалуйста, попробуйте выбрать другие товары или дождитесь поступления."
                button={
                    <DrawerBottomFixed>
                        <DrawerButton href={companyShopUrl}>Вернуться в магазин</DrawerButton>
                    </DrawerBottomFixed>
                }
            />
        );
    }

    return (
        <Drawer snapPoints={[0.75]} {...other}>
            <VStack className={styles.modal}>
                <VStack className={styles.container}>
                    <DrawerHeader title={TITILE} />

                    <VStack gap="4">
                        {insufficientItems.map((insufficientItem) => {
                            const orderItem = orderItems.find(
                                (item) => item.product_variant_id === insufficientItem.id
                            );

                            if (!orderItem) {
                                return null;
                            }

                            return (
                                <OutdatedStockItem
                                    key={orderItem.product_variant_id}
                                    name={orderItem.name}
                                    image={getProductImage(orderItem.media[0])}
                                    quantity={insufficientItem.available}
                                    quantityOld={orderItem.quantity}
                                    size={getProductSize(orderItem)}
                                />
                            );
                        })}
                    </VStack>
                    {errorText && (
                        <Text color="red" size="xs">
                            {errorText}
                        </Text>
                    )}
                </VStack>

                <DrawerBottomFixed>
                    <HStack gap="8">
                        <DrawerButton className={styles.button} href={companyShopUrl}>
                            Вернуться в магазин
                        </DrawerButton>
                        <DrawerButton className={styles.button} onClick={props.onUpdateOrder}>
                            Обновить корзину
                        </DrawerButton>
                    </HStack>
                </DrawerBottomFixed>
            </VStack>
        </Drawer>
    );
};
